import * as React from 'react';
import MainSectionLayout from '../layout/MainSectionLayout';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { Page, TLCode } from '../../common/enums';
import { Instance } from 'mobx-state-tree';
import {
  ApplicationStore,
  AuthenticationStore,
  BankHolidayStore,
  BankHolidayDateStore,
  GroupStore,
  PharmacyAnnualReviewStore,
  PharmacyDetailedStore,
  PharmacyStore,
  SecretariatStore,
  SystemStatusStore,
  PharmacyStatusStore,
  PharmacyInconsistencyHistoryStore,
  PharmacyInconsistencyFieldTypeStore,
} from '../../store';
import { inject, observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Secretariat } from '../../store/models/Secretariat';
import { css } from 'emotion';
import { Card, Tab, Tabs } from '@blueprintjs/core';
import BankHolidayDateManager from './BankHolidayDateManager';
import PlanningUpload from './PlanningUpload';
import DutyUpload from './DutyUpload';
import AnnualReviewManager from './AnnualReviewManager';
import PrefManager from './PrefManager';
import DutyDownload from './DutyDownload';
import moment from 'moment';
import { PharmacyAnnualReview } from '../../store/models/PharmacyAnnualReview';
import { PharmacyAnnualReviewSearchCriteria } from '../../store/PharmacyAnnualReviewStore';
import { Pharmacy } from '../../store/models/Pharmacy';
import { Group } from '../../store/models/Group';
import BlackPointManagerV2 from './BlackPointManagerV2';
import MessageTabV2 from './MessageTabV2';
import MessageTabV3 from './MessageTabV3';
import MessageTabV4 from './MessageTabV4';
import { Message } from '../../store/models/Message';
import { MessageStore } from '../../store/MessageStore';
import { UiStore } from '../../store';
import { BankHolidayDate } from '../../store/models';
import { DiscrepancyPage } from './discrepancy/DiscrepancyPage';
import MediaSanteApiCheck from './MediaSanteApiCheck';
import { Semester } from '../../common/utils';

const DashboardContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  '& .bp3-tabs': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  '& .bp3-tab-panel': {
    flex: 1,
  },
  '& .ag-theme-balham': {
    width: '100%',
  },
});

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
});

const CardStyle = css({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

interface SecretariatDashboardProps extends WithTranslateProps {
  ApplicationStore?: Instance<typeof ApplicationStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  BankHolidayStore?: Instance<typeof BankHolidayStore>;
  BankHolidayDateStore?: Instance<typeof BankHolidayDateStore>;
  GroupStore?: Instance<typeof GroupStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
  SystemStatusStore?: Instance<typeof SystemStatusStore>;
  PharmacyAnnualReviewStore?: Instance<typeof PharmacyAnnualReviewStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
  MessageStore?: Instance<typeof MessageStore>;
  PharmacyInconsistencyHistoryStore?: Instance<typeof PharmacyInconsistencyHistoryStore>;
  PharmacyInconsistencyFieldTypeStore?: Instance<typeof PharmacyInconsistencyFieldTypeStore>;
  PharmacyStatusStore?: Instance<typeof PharmacyStatusStore>;
  UiStore?: Instance<typeof UiStore>;
}

interface SecretariatDashboardState {
  isLoading: boolean;
  secretariatId?: number;
  annualReviews: Array<Instance<typeof PharmacyAnnualReview>>;
  annualReviewSearchCriteria?: Instance<typeof PharmacyAnnualReviewSearchCriteria>;
  pharmacyList: Array<Instance<typeof Pharmacy>>;
  groupList: Array<Instance<typeof Group>>;
  missingBlackpointSearchCriteria: any;
  missingBlackpoint: any[];
  bankHolidayDates: any[];
  bankHolidayList: any[];
  messageList: Array<Instance<typeof Message>>;
  filter: any;
  activeFilter: 'validated' | 'calculated' | 'all';
  currentTab?: DashboardTab;
  sysparams?: any;
  systemStatus?: any;
  appSettings?: any;
  completionLevelFilter: 'all' | 'some' | 'none' | 'any';
  levelFilter: 'one' | 'three' | 'both';
  tcOrSpecialBpFilter: 'all' | 's1' | 's2';
}

enum DashboardTab {
  Main = 'main',
  BankHoliday = 'bankHoliday',
  AnnualReview = 'annualReview',
  Prefs = 'prefs',
  MissingBlackPoint = 'missingBlackPoint',
  Message = 'message',
  PlanningUpload = 'planningUpload',
  Message3 = 'message3',
  DutyUpload = 'dutyUpload',
  DutyDownload = 'dutyDownload',
  BankHolidayDates = 'bankHolidayDates',
  System = 'system',
  Discrepencies = 'discrepencies',
  Mediasante = 'Mediasante',
}

@inject('ApplicationStore')
@inject('AuthenticationStore')
@inject('BankHolidayStore')
@inject('BankHolidayDateStore')
@inject('GroupStore')
@inject('SecretariatStore')
@inject('SystemStatusStore')
@inject('PharmacyAnnualReviewStore')
@inject('PharmacyStore')
@inject('PharmacyDetailedStore')
@inject('MessageStore')
@inject('PharmacyInconsistencyHistoryStore')
@inject('PharmacyInconsistencyFieldTypeStore')
@inject('PharmacyStatusStore')
@inject('UiStore')
@observer
class SecretariatDashboard extends React.Component<SecretariatDashboardProps, SecretariatDashboardState> {
  state = {
    isLoading: false,
    annualReviews: [],
    pharmacyList: [],
    groupList: [],
    missingBlackpointSearchCriteria: null,
    missingBlackpoint: [],
    bankHolidayDates: [],
    bankHolidayList: [],
    messageList: [],
    filter: '',
    activeFilter: 'calculated',
    completionLevelFilter: 'any',
    levelFilter: 'three',
    tcOrSpecialBpFilter: 'all',
    currentTab: undefined,
  } as SecretariatDashboardState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    this._isMounted && this.setState({ isLoading: true });
    const {
      AuthenticationStore,
      ApplicationStore,
      translate,
      SecretariatStore,
      GroupStore,
      PharmacyInconsistencyHistoryStore,
      PharmacyInconsistencyFieldTypeStore,
    } = this.props;
    if (AuthenticationStore!.isSecretariat) {
      if (AuthenticationStore!.secretariatId === null) {
        ApplicationStore!.navigate(`${Page.Error}/${translate(TLCode.ERROR_NoSecretariatRelatedToThisUser)}`);
      } else {
        await this.setSecretariat(AuthenticationStore!.secretariatId);
      }
    } else {
      await SecretariatStore!.checkListLoaded(); // loadList();
    }
    await Promise.all([
      GroupStore!.checkListLoaded(),
      PharmacyInconsistencyHistoryStore!.checkListLoaded(),
      PharmacyInconsistencyFieldTypeStore!.checkListLoaded(),
    ]); // loadList();
    this._isMounted && this.setState({ isLoading: false });
  }

  async setSecretariat(secretariatId: number) {
    await this.loadPharmacyList();
    this._isMounted && this.props.UiStore!.setPharmacyCalendarYear(moment().year() + 1);
    this._isMounted &&
      this.setState(
        {
          secretariatId,
          annualReviews: [],
          missingBlackpointSearchCriteria: {
            secretariatId: this.state.secretariatId,
            year: moment().year() + 1,
            filter: 'INCOMPLETE',
          },
          missingBlackpoint: [],
          messageList: [],
        },
        async () => {
          if (this.state.currentTab) await this.loadTabData(this.state.currentTab);
        },
      );
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  loadTabData = async (tabId: DashboardTab) => {
    switch (tabId) {
      case DashboardTab.Main:
        break;
      case DashboardTab.BankHoliday:
        break;
      case DashboardTab.Prefs:
        await this.loadPharmacyList();
        break;
      case DashboardTab.AnnualReview:
        await this.loadAnnualReview();
        break;
      case DashboardTab.MissingBlackPoint:
        await this.loadMissingBlackPoint();
        break;
      case DashboardTab.Message:
        await this.loadPharmacyList();
        break;
      case DashboardTab.DutyUpload:
        break;
      case DashboardTab.DutyDownload:
        break;
      case DashboardTab.BankHolidayDates:
        await this.loadBankHolidayList();
        break;
      case DashboardTab.System:
        // sysparams
        // systemStatus
        await this.loadSystemStatus();
        // appSettings
        await this.loadBackendSettings();
        break;
      case DashboardTab.Discrepencies:
        break;
    }
    this._isMounted && this.setState({ currentTab: tabId });
  };

  async loadAnnualReview() {
    if (this.state.annualReviews.length > 0) return;
    const criteria: any = {
      secretariatId: this.state.secretariatId,
      year: moment().year() + 1,
      validated: false,
      status: 'Calculated',
      filter: 'all',
    };
    this.props.PharmacyAnnualReviewStore!.clearData();
    const response = await this.props.PharmacyAnnualReviewStore!.search(criteria);
    this._isMounted &&
      this.setState({
        // @ts-ignore
        annualReviews: response,
        annualReviewSearchCriteria: criteria,
      });
  }

  async loadMissingBlackPoint() {
    if (this.state.missingBlackpoint.length > 0) return;
    const criteria: any = {
      secretariatId: this.state.secretariatId,
      year: moment().year() + 1,
      filter: 'INCOMPLETE',
    };
    this._isMounted &&
      (await this.setState({
        missingBlackpointSearchCriteria: criteria,
      }));
    await this.searchMissingBlackPoint();
  }

  async searchMissingBlackPoint() {
    const response = await this.props.PharmacyDetailedStore!.getMissingHolidays(
      this.state.missingBlackpointSearchCriteria,
    );
    this._isMounted &&
      this.setState({
        // @ts-ignore
        missingBlackpoint: response,
      });
  }

  navigateMissingBlackpoint = async (direction) => {
    let searchCriteria = this.state.missingBlackpointSearchCriteria;
    if (direction === 0) {
      searchCriteria.year = moment().year();
    } else {
      searchCriteria.year = searchCriteria.year + direction;
    }
    this.props.UiStore!.setPharmacyCalendarYear(searchCriteria.year);
    this.setState({ missingBlackpointSearchCriteria: searchCriteria });
    await this.searchMissingBlackPoint();
  };

  async loadBankHolidayList() {
    if (this.state.bankHolidayList.length > 0) return;
    const response = await this.props.BankHolidayStore!.loadList();
    this._isMounted &&
      this.setState({
        // @ts-ignore
        bankHolidayList: response,
      });
  }

  async loadSystemStatus() {
    if (this.state.systemStatus != undefined) return;
    const response = await this.props.ApplicationStore!.getSystemStatus();
    this._isMounted &&
      this.setState({
        // @ts-ignore
        systemStatus: response,
      });
  }

  async loadBackendSettings() {
    if (this.state.appSettings != undefined) return;
    const response = await this.props.ApplicationStore!.getBackendSettings();
    this._isMounted &&
      this.setState({
        // @ts-ignore
        appSettings: response,
      });
  }

  onMissingBlackPointSearch = async (searchData: any) => {
    const res: any = await this.props.PharmacyDetailedStore!.getMissingHolidays(
      this.state.missingBlackpointSearchCriteria,
    );
    this._isMounted &&
      this.setState({
        missingBlackpoint: res,
      });
  };

  loadPharmacyList = async () => {
    await this.props.PharmacyStore!.checkListLoaded(); //loadList();
    this._isMounted && this.setState({ pharmacyList: this.props.PharmacyStore!.dataList });
  };

  loadGroupList = async () => {
    const res: any = await this.props.GroupStore!.dataList;
    this._isMounted && this.setState({ groupList: res });
  };

  onSecretariatSelected = async (value: Instance<typeof Secretariat>) => {
    await this.setSecretariat(value.secretariatId);
  };

  onSaveStatus = async (data: any) => {
    await this.props.SystemStatusStore!.saveElement(data);
    await this.setSecretariat(this.state.secretariatId!);
  };

  onSaveBankHolidayDate = async (data: any) => {
    await this.props.BankHolidayDateStore!.saveElement(data);
  };

  onDeleteBankHolidayDate = async (data: Instance<typeof BankHolidayDate>) => {
    const bhd = this.props.BankHolidayDateStore!.getById(data.bankHolidayDateId);
    this.props.BankHolidayDateStore!.deleteElement(bhd);
  };

  onValidateReview = async (data: any) => {
    await this.props.PharmacyAnnualReviewStore!.validateReview(data);
    const res: any = await this.props.PharmacyAnnualReviewStore!.search(this.state.annualReviewSearchCriteria!);
    this._isMounted &&
      this.setState({
        annualReviews: res,
      });
  };

  onAnnualReviewSearch = async (searchData: any) => {
    const res: any = await this.props.PharmacyAnnualReviewStore!.search(searchData);
    this._isMounted &&
      this.setState({
        annualReviews: res,
        annualReviewSearchCriteria: searchData,
      });
  };

  setFilterText = (filter) => {
    this.setState({
      filter,
    });
  };

  getSecretariatId = () => {
    if (this.props.AuthenticationStore!.isSuperAdmin)
      return this.props.SecretariatStore!.selectedSecretariatId === 0
        ? undefined
        : this.props.SecretariatStore!.selectedSecretariatId;
    if (this.props.AuthenticationStore!.isSecretariat) return this.props.AuthenticationStore!.secretariatId;
    return undefined;
  };

  showPharmacy = (pharmacy: any) => {
    return (
      (this.props.AuthenticationStore!.isSuperAdmin && this.props.SecretariatStore!.selectedSecretariatId === 0) ||
      (this.props.AuthenticationStore!.isSuperAdmin &&
        pharmacy !== undefined &&
        this.props.SecretariatStore!.selectedSecretariatId !== undefined &&
        this.props.SecretariatStore!.selectedSecretariatId === pharmacy!.secretariatId) ||
      (pharmacy !== undefined &&
        this.props.AuthenticationStore!.isSecretariat &&
        this.props.AuthenticationStore!.secretariatId === pharmacy!.secretariatId)
    );
  };

  public render() {
    const { AuthenticationStore, ApplicationStore, GroupStore, translate } = this.props;
    const {
      missingBlackpoint,
      bankHolidayDates,
      bankHolidayList,
      annualReviews,
      annualReviewSearchCriteria,
      isLoading,
      missingBlackpointSearchCriteria,
      pharmacyList,
      filter,
      activeFilter,
      completionLevelFilter,
      levelFilter,
      tcOrSpecialBpFilter,
      appSettings,
      systemStatus,
    } = this.state;

    return (
      <MainSectionLayout title={TLCode.SECRETARIAT_DASHBOARD_Title} isLoading={isLoading}>
        <Container>
          <Card className={CardStyle.toString()}>
            {
              <DashboardContainer>
                <Tabs
                  id={DashboardTab.Main}
                  defaultSelectedTabId="level1"
                  onChange={this.loadTabData}
                  renderActiveTabPanelOnly={true}
                >
                  {AuthenticationStore!.isAdmin && (
                    <Tab
                      id={DashboardTab.BankHoliday}
                      title={translate(TLCode.SECRETARIAT_DASHBOARD_BankHolidayTitle)}
                    />
                  )}
                  <Tab
                    id={DashboardTab.Prefs}
                    title={translate(TLCode.SECRETARIAT_DASHBOARD_PrefsTitle)}
                    panel={
                      <PrefManager
                        pharmacyList={pharmacyList.filter(this.showPharmacy)}
                        onReviewSave={this.onValidateReview}
                        searchCriteria={annualReviewSearchCriteria}
                        onSearch={this.onAnnualReviewSearch}
                        activeFilter={activeFilter}
                        setActiveFilter={(filter) => this.setState({ activeFilter: filter })}
                        filter={filter}
                        setFilterText={this.setFilterText}
                      />
                    }
                  />
                  {AuthenticationStore!.isAdmin && (
                    <Tab
                      id={DashboardTab.AnnualReview}
                      title={translate(TLCode.SECRETARIAT_DASHBOARD_AnnualReviewTitle)}
                      panel={
                        <AnnualReviewManager
                          annualReviews={annualReviews}
                          onReviewSave={this.onValidateReview}
                          searchCriteria={annualReviewSearchCriteria}
                          onSearch={this.onAnnualReviewSearch}
                          activeFilter={activeFilter}
                          setActiveFilter={(filter) => this.setState({ activeFilter: filter })}
                          filter={filter}
                          setFilterText={this.setFilterText}
                          secretariatId={this.getSecretariatId()}
                        />
                      }
                    />
                  )}
                  <Tab
                    id={DashboardTab.MissingBlackPoint}
                    title={translate(TLCode.MISSING_BLACKPOINTS_Title)}
                    panel={
                      <BlackPointManagerV2
                        missingBlackPoint={missingBlackpoint}
                        searchCriteria={missingBlackpointSearchCriteria}
                        onSearch={this.onMissingBlackPointSearch}
                        onChangeCriteria={this.navigateMissingBlackpoint}
                        pharmacyList={pharmacyList.filter(this.showPharmacy)}
                        navigate={ApplicationStore!.navigate}
                        completionLevelFilter={completionLevelFilter}
                        levelFilter={levelFilter}
                        tcOrSpecialBpFilter={tcOrSpecialBpFilter}
                        setHolCntFilter={(filter) => this.setState({ completionLevelFilter: filter })}
                        setLevelFilter={(filter) => this.setState({ levelFilter: filter })}
                        setTcOrSpecialBpFilter={(filter) => this.setState({ tcOrSpecialBpFilter: filter })}
                      />
                    }
                  />
                  {/* {(AuthenticationStore!.isAdmin || AuthenticationStore!.isSecretariat) && (
                    <Tab
                      id='Message'
                      title={translate(TLCode.MESSAGE_TabTitle)}
                      panel={
                        <MessageTabV2
                          secretariatId={this.getSecretariatId()}
                          pharmacyList={pharmacyList.filter(this.showPharmacy)}
                          groupList={GroupStore!.dataList}
                        />
                      }
                    />
                  )}
                  {(AuthenticationStore!.isAdmin || AuthenticationStore!.isSecretariat) && (
                    <Tab
                      id={DashboardTab.Message3}
                      title={translate(TLCode.MESSAGE_TabTitle)}
                      panel={
                        <MessageTabV3
                          secretariatId={this.getSecretariatId()}
                          pharmacyList={pharmacyList.filter(this.showPharmacy)}
                          groupList={GroupStore!.dataList}
                        />
                      }
                    />
                  )} */}
                  {(AuthenticationStore!.isAdmin || AuthenticationStore!.isSecretariat) && (
                    <Tab
                      id={DashboardTab.Message}
                      title={translate(TLCode.MESSAGE_TabTitle)}
                      panel={
                        <MessageTabV4
                          secretariatId={this.getSecretariatId()}
                          pharmacyList={pharmacyList.filter(this.showPharmacy)}
                          groupList={GroupStore!.dataList}
                        />
                      }
                    />
                  )}
                  {(AuthenticationStore!.isAdmin || AuthenticationStore!.isSecretariat) && (
                    <Tab
                      id={DashboardTab.PlanningUpload}
                      title={translate(TLCode.PLANNINGUPLOAD_Title)}
                      panel={<PlanningUpload />}
                    />
                  )}
                  {(AuthenticationStore!.isAdmin || AuthenticationStore!.isSecretariat) && (
                    <Tab
                      id={DashboardTab.DutyUpload}
                      title={translate(TLCode.DUTYUPLOAD_Title)}
                      panel={<DutyUpload />}
                    />
                  )}
                  {(AuthenticationStore!.isAdmin || AuthenticationStore!.isSecretariat) && (
                    <Tab
                      id={DashboardTab.DutyDownload}
                      title={translate(TLCode.DUTYDOWNLOAD_Title)}
                      panel={<DutyDownload />}
                    />
                  )}
                  {(AuthenticationStore!.isSuperAdmin || AuthenticationStore!.isSecretariat) && (
                    <Tab
                      id={DashboardTab.BankHolidayDates}
                      title={translate(TLCode.BANK_HOLIDAY_DATES_Title)}
                      panel={
                        <BankHolidayDateManager
                          bankHolidayList={bankHolidayList}
                          bankHolidayDateList={this.props.BankHolidayDateStore!.dataList}
                          onBankHolidayDateSave={this.onSaveBankHolidayDate}
                          onBankHolidayDateDelete={this.onDeleteBankHolidayDate}
                        />
                      }
                    />
                  )}
                  {AuthenticationStore!.isSuperAdmin && (
                    <Tab
                      id={DashboardTab.System}
                      title={translate(TLCode.SECRETARIAT_DASHBOARD_SystemTitle)}
                      panel={
                        <>
                          <div>
                            <b>System status</b>
                            <div style={{ marginLeft: '30px' }}>
                              <b>Hangfire status</b>:
                              {systemStatus != undefined
                                ? systemStatus.find((item) => item.subSystem === 'HangfireStatus').status
                                : ''}
                            </div>
                            <div style={{ marginLeft: '30px' }}>
                              <b>Next accepted BP</b>:
                              {systemStatus != undefined
                                ? systemStatus.find((item) => item.subSystem === 'NextAcceptableBlackpoint').status
                                : ''}
                            </div>
                            <div style={{ marginLeft: '30px' }}>
                              <b>Access</b>:
                              {systemStatus != undefined
                                ? systemStatus.find((item) => item.subSystem === 'AccessRights').status
                                : ''}
                            </div>
                          </div>
                          <div>
                            <b>AppSettings</b>
                            <div style={{ marginLeft: '30px' }}>
                              <b>Database</b>
                              <div style={{ marginLeft: '30px' }}>
                                <b>ConnectionStrings:</b>
                                {appSettings != undefined ? appSettings.database.connectionStrings.webGardes : ''}
                              </div>
                            </div>
                            <div>
                              <b>General</b>
                              <div style={{ marginLeft: '30px' }}>
                                <b>TempDir:</b>
                                {appSettings != undefined ? appSettings.general.tempDir : ''}
                              </div>
                            </div>
                            <div>
                              <b>Transfer module</b>
                              <div style={{ marginLeft: '30px' }}>
                                <b>mode:</b>
                                {appSettings != undefined ? appSettings.transferModuleMode.mode : ''}
                              </div>
                              <div style={{ marginLeft: '30px' }}>
                                <b>notifications:</b>
                                {appSettings != undefined ? appSettings.transferModuleMode.notifications : ''}
                              </div>
                            </div>
                            <div>
                              <b>Pharmamove module</b>
                              <div style={{ marginLeft: '30px' }}>
                                <b>from:</b>
                                {appSettings != undefined ? appSettings.pharmamoveModule.from : ''}
                              </div>
                              <div style={{ marginLeft: '30px' }}>
                                <b>notifications:</b>
                                <div style={{ marginLeft: '30px' }}>
                                  <b>to APB:</b>
                                  {appSettings != undefined ? appSettings.pharmamoveModule.notifications.toAPB : ''}
                                </div>
                                <div style={{ marginLeft: '30px' }}>
                                  <b>to secretariats:</b>
                                  {appSettings != undefined
                                    ? appSettings.pharmamoveModule.notifications.toSecretariats
                                    : ''}
                                </div>
                                <div style={{ marginLeft: '30px' }}>
                                  <b>to pharmacies:</b>
                                  {appSettings != undefined
                                    ? appSettings.pharmamoveModule.notifications.toPharmacies
                                    : ''}
                                </div>
                                <div style={{ marginLeft: '30px' }}>
                                  <b>to level 0:</b>
                                  {appSettings != undefined ? appSettings.pharmamoveModule.notifications.toLevel0 : ''}
                                </div>
                              </div>
                            </div>
                            <div>
                              <b>AFMPS</b>
                              <div style={{ marginLeft: '30px' }}>
                                <b>Authorized pharmacies:</b>
                                {appSettings != undefined ? appSettings.afmps.authorizedPharmacies : ''}
                              </div>
                              <div style={{ marginLeft: '30px' }}>
                                <b>Transfer requests:</b>
                                {appSettings != undefined ? appSettings.afmps.transferRequests : ''}
                              </div>
                            </div>
                            <div>
                              <b>APB</b>
                              <div style={{ marginLeft: '30px' }}>
                                <b>Email:</b>
                                {appSettings != undefined ? appSettings.apb.email : ''}
                              </div>
                            </div>
                            <div>
                              <b>Mail</b>
                              <div style={{ marginLeft: '30px' }}>
                                <b>redirection:</b>
                                {appSettings != undefined ? appSettings.mail.redirection : ''}
                              </div>
                              <div style={{ marginLeft: '30px' }}>
                                <b>redirectionAddress:</b>
                                {appSettings != undefined ? appSettings.mail.redirectionAddress : ''}
                              </div>
                              <div style={{ marginLeft: '30px' }}>
                                <b>noReplySender:</b>
                                {appSettings != undefined ? appSettings.mail.noReplySender : ''}
                              </div>
                              <div style={{ marginLeft: '30px' }}>
                                <b>from:</b>
                                {appSettings != undefined ? appSettings.mail.from : ''}
                              </div>
                              <div style={{ marginLeft: '30px' }}>
                                <b>sender:</b>
                                {appSettings != undefined ? appSettings.mail.sender : ''}
                              </div>
                              <div style={{ marginLeft: '30px' }}>
                                <b>replyTo:</b>
                                {appSettings != undefined ? appSettings.mail.replyTo : ''}
                              </div>
                              <div style={{ marginLeft: '30px' }}>
                                <b>support:</b>
                                {appSettings != undefined ? appSettings.mail.support : ''}
                              </div>
                            </div>
                            <div>
                              <b>Hangfire</b>
                              <div style={{ marginLeft: '30px' }}>
                                <b>enabled:</b>
                                {appSettings != undefined ? appSettings.hangfire.enabled : ''}
                              </div>
                            </div>
                          </div>
                        </>
                      }
                    />
                  )}

                  {(AuthenticationStore!.isSuperAdmin ||
                    AuthenticationStore!.isAdmin ||
                    AuthenticationStore!.isSecretariat) && (
                    <Tab
                      id={DashboardTab.Discrepencies}
                      title={translate(TLCode.SECRETARIAT_DASHBOARD_Discrepancy_Tab)}
                      panel={
                        <DiscrepancyPage
                          discrepancies={this.props.PharmacyInconsistencyHistoryStore!.dataList}
                          translate={translate}
                          pharmacyStatuses={this.props.PharmacyStatusStore!.dataList}
                          ApplicationStore={this.props.ApplicationStore}
                          PharmacyStore={this.props.PharmacyStore}
                        />
                      }
                    />
                  )}

                  {AuthenticationStore!.isSuperAdmin && (
                    <Tab
                      id={DashboardTab.Mediasante}
                      title={translate(TLCode.SECRETARIAT_DASHBOARD_Mediasante)}
                      panel={<MediaSanteApiCheck />}
                    />
                  )}
                </Tabs>
              </DashboardContainer>
            }
          </Card>
        </Container>
      </MainSectionLayout>
    );
  }
}

export default withTranslate(SecretariatDashboard);
